// WbCompliancePage — /weighbridge/settings/compliance.
//
// Phase C6. Surfaces the C6-owned fields on wb_company_settings (see
// WEIGHBRIDGE_SERVER_CONTRACT.md §C6 for the authoritative field list).
//
// IMPORTANT: this page MUST NOT include the VAT fields in its PATCH body
// — the C5 VAT Settings page owns those, and the two pages share the
// same endpoint (PATCH /wb/company-settings). Partial PATCH keeps each
// page's saves scoped to its own fields so a stale snapshot on the
// other surface can't silently overwrite a recent change.
//
// Fields from the C0 design that DON'T exist on the server (numeric
// approval thresholds, ANPR confidence company defaults, ticket-numbering
// format, hazardous compliance template fields, advice-note email
// subject/body customisation) are deliberately not rendered — see the
// "Server gaps" section in the contract doc. The Ticket numbering card
// is the one exception: we render it as a disabled "Auto-generated"
// placeholder because tenants who came from the design doc will look
// for it; doing so makes the gap explicit instead of just absent.

import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { useToastStore } from '../../../store/toastStore';
import { useWbCompanySettings } from '../context/WbCompanySettingsContext';
import {
  useUpdateWbCompanySettings,
  type UpdateWbCompanySettingsInput,
} from '../hooks/useWbCompanySettings';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbCompliancePage.module.css';

type CreditLimitBehaviour = 'block' | 'warn' | 'off';

interface FormState {
  credit_limit_behaviour: CreditLimitBehaviour;
  trade_approved_default: boolean;
  manual_weight_requires_reason_hw_online: boolean;
  manual_weight_requires_reason_hw_offline: boolean;
  stale_tare_threshold_days: string;
  compliance_wtn_enabled: boolean;
  compliance_hazardous_enabled: boolean;
  compliance_carrier_licence_required: boolean;
  compliance_ewc_required_on_waste_lines: boolean;
  stock_alert_email_recipients: string[];
  weight_reconciliation_tolerance_pct: string;
  autosave_draft_interval_sec: string;
}

const EMPTY_FORM: FormState = {
  credit_limit_behaviour: 'warn',
  trade_approved_default: false,
  manual_weight_requires_reason_hw_online: true,
  manual_weight_requires_reason_hw_offline: true,
  stale_tare_threshold_days: '30',
  compliance_wtn_enabled: false,
  compliance_hazardous_enabled: false,
  compliance_carrier_licence_required: false,
  compliance_ewc_required_on_waste_lines: false,
  stock_alert_email_recipients: [],
  weight_reconciliation_tolerance_pct: '2.0',
  autosave_draft_interval_sec: '15',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function WbCompliancePage(): JSX.Element {
  const settingsCtx = useWbCompanySettings();
  const settings = settingsCtx.settings;
  const updateSettings = useUpdateWbCompanySettings();
  const pushToast = useToastStore((s) => s.push);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Hydrate form once settings load.
  useEffect(() => {
    if (!settings) return;
    setForm({
      credit_limit_behaviour: settings.credit_limit_behaviour,
      trade_approved_default: settings.trade_approved_default,
      manual_weight_requires_reason_hw_online:
        settings.manual_weight_requires_reason_hw_online,
      manual_weight_requires_reason_hw_offline:
        settings.manual_weight_requires_reason_hw_offline,
      stale_tare_threshold_days: String(settings.stale_tare_threshold_days),
      compliance_wtn_enabled: settings.compliance_wtn_enabled,
      compliance_hazardous_enabled: settings.compliance_hazardous_enabled,
      compliance_carrier_licence_required:
        settings.compliance_carrier_licence_required,
      compliance_ewc_required_on_waste_lines:
        settings.compliance_ewc_required_on_waste_lines,
      stock_alert_email_recipients: [...settings.stock_alert_email_recipients],
      weight_reconciliation_tolerance_pct: String(
        settings.weight_reconciliation_tolerance_pct,
      ),
      autosave_draft_interval_sec: String(settings.autosave_draft_interval_sec),
    });
  }, [settings]);

  function validate(): boolean {
    const errs: Record<string, string> = {};

    // stale_tare_threshold_days: 1–365
    const tare = parseInt(form.stale_tare_threshold_days, 10);
    if (!Number.isFinite(tare) || tare < 1 || tare > 365) {
      errs['stale_tare_threshold_days'] =
        'Stored tare validity must be between 1 and 365 days.';
    }

    // weight_reconciliation_tolerance_pct: 0.1–10.0
    const tol = parseFloat(form.weight_reconciliation_tolerance_pct);
    if (!Number.isFinite(tol) || tol < 0.1 || tol > 10) {
      errs['weight_reconciliation_tolerance_pct'] =
        'Tolerance must be between 0.1 and 10.0 percent.';
    }

    // autosave_draft_interval_sec: 1–60
    const auto = parseInt(form.autosave_draft_interval_sec, 10);
    if (!Number.isFinite(auto) || auto < 1 || auto > 60) {
      errs['autosave_draft_interval_sec'] =
        'Autosave interval must be between 1 and 60 seconds.';
    }

    // stock_alert_email_recipients: ≤10 entries, each a valid email
    if (form.stock_alert_email_recipients.length > 10) {
      errs['stock_alert_email_recipients'] =
        'Up to 10 stock alert recipients are allowed.';
    }
    const badEmail = form.stock_alert_email_recipients.find(
      (r) => r.trim() !== '' && !EMAIL_REGEX.test(r.trim()),
    );
    if (badEmail !== undefined) {
      errs['stock_alert_email_recipients'] =
        'One or more email addresses are invalid.';
    }

    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function buildBody(): UpdateWbCompanySettingsInput {
    // C6-owned fields ONLY. The C5 (VAT) page handles vat_enabled,
    // vat_registration_number, and default_vat_rate_id.
    return {
      credit_limit_behaviour: form.credit_limit_behaviour,
      trade_approved_default: form.trade_approved_default,
      manual_weight_requires_reason_hw_online:
        form.manual_weight_requires_reason_hw_online,
      manual_weight_requires_reason_hw_offline:
        form.manual_weight_requires_reason_hw_offline,
      stale_tare_threshold_days: parseInt(form.stale_tare_threshold_days, 10),
      compliance_wtn_enabled: form.compliance_wtn_enabled,
      compliance_hazardous_enabled: form.compliance_hazardous_enabled,
      compliance_carrier_licence_required:
        form.compliance_carrier_licence_required,
      compliance_ewc_required_on_waste_lines:
        form.compliance_ewc_required_on_waste_lines,
      stock_alert_email_recipients: form.stock_alert_email_recipients
        .map((r) => r.trim())
        .filter((r) => r !== ''),
      weight_reconciliation_tolerance_pct: parseFloat(
        form.weight_reconciliation_tolerance_pct,
      ),
      autosave_draft_interval_sec: parseInt(
        form.autosave_draft_interval_sec,
        10,
      ),
    };
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    updateSettings.mutate(buildBody(), {
      onSuccess: () => {
        settingsCtx.refetch();
        pushToast({ type: 'success', message: 'Compliance settings saved.' });
      },
      onError: (err) => setError(extractErrorMessage(err)),
    });
  }

  function addRecipient() {
    if (form.stock_alert_email_recipients.length >= 10) return;
    setForm((prev) => ({
      ...prev,
      stock_alert_email_recipients: [...prev.stock_alert_email_recipients, ''],
    }));
  }

  function updateRecipient(idx: number, value: string) {
    setForm((prev) => ({
      ...prev,
      stock_alert_email_recipients: prev.stock_alert_email_recipients.map(
        (r, i) => (i === idx ? value : r),
      ),
    }));
  }

  function removeRecipient(idx: number) {
    setForm((prev) => ({
      ...prev,
      stock_alert_email_recipients: prev.stock_alert_email_recipients.filter(
        (_, i) => i !== idx,
      ),
    }));
  }

  if (settingsCtx.isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader title="Compliance & Policies" />
        <Card>
          <div className={styles.loading}>
            <Spinner label="Loading settings" />
          </div>
        </Card>
      </div>
    );
  }

  if (settingsCtx.isError) {
    return (
      <div className={styles.page}>
        <PageHeader title="Compliance & Policies" />
        <Card>
          <p className={styles.error}>Could not load company settings.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Compliance & Policies"
        subtitle="Operational policy and compliance defaults"
      />

      {/* noValidate: our handleSubmit owns validation. Letting HTML5
          constraint validation fire first would suppress the submit
          event for numbers outside min/max, preventing our own
          field-error UI from rendering. */}
      <form
        noValidate
        onSubmit={handleSubmit}
        className={styles.cardStack}
      >
        {/* -- Ticket numbering ------------------------------------------- */}
        <Card>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Ticket numbering</h3>
            <label className={styles.field}>
              <span>Ticket number format</span>
              <input
                type="text"
                value="Auto-generated"
                disabled
                readOnly
                aria-label="Ticket number format"
              />
              <span className={styles.fieldHelp}>
                Ticket numbers are generated automatically when a ticket is
                completed. Contact support if you need a custom prefix.
              </span>
            </label>
          </div>
        </Card>

        {/* -- Credit & approval defaults --------------------------------- */}
        <Card>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              Credit &amp; approval defaults
            </h3>
            <div className={styles.grid}>
              <label className={styles.field}>
                <span>When a customer hits their credit limit</span>
                <select
                  value={form.credit_limit_behaviour}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      credit_limit_behaviour: e.target
                        .value as CreditLimitBehaviour,
                    }))
                  }
                >
                  <option value="block">Block ticket completion</option>
                  <option value="warn">Show warning</option>
                  <option value="off">No action</option>
                </select>
              </label>
            </div>
            <label className={styles.toggleRow}>
              <input
                type="checkbox"
                checked={form.trade_approved_default}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    trade_approved_default: e.target.checked,
                  }))
                }
              />
              <span>New tickets default to &ldquo;trade-approved&rdquo;</span>
            </label>
          </div>
        </Card>

        {/* -- Manual weight reason requirements -------------------------- */}
        <Card>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              Manual weight overrides
            </h3>
            <p className={styles.sectionHelp}>
              When the operator types a weight instead of taking it from the
              bridge, require a reason in the following cases.
            </p>
            <div className={styles.toggleStack}>
              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.manual_weight_requires_reason_hw_online}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      manual_weight_requires_reason_hw_online:
                        e.target.checked,
                    }))
                  }
                />
                <span>Require reason when the bridge IS online</span>
              </label>
              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.manual_weight_requires_reason_hw_offline}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      manual_weight_requires_reason_hw_offline:
                        e.target.checked,
                    }))
                  }
                />
                <span>Require reason when the bridge is offline</span>
              </label>
            </div>
          </div>
        </Card>

        {/* -- Weight policy --------------------------------------------- */}
        <Card>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Weight policy</h3>
            <div className={styles.grid}>
              <label className={styles.field}>
                <span>Stored tare validity (days)</span>
                <input
                  type="number"
                  min={1}
                  max={365}
                  step={1}
                  value={form.stale_tare_threshold_days}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      stale_tare_threshold_days: e.target.value,
                    }))
                  }
                />
                <span className={styles.fieldHelp}>
                  Stored vehicle tare weights older than this are flagged as
                  stale on the ticket.
                </span>
                {fieldErrors['stale_tare_threshold_days'] ? (
                  <span className={styles.fieldError}>
                    {fieldErrors['stale_tare_threshold_days']}
                  </span>
                ) : null}
              </label>

              <label className={styles.field}>
                <span>Reconciliation tolerance (%)</span>
                <input
                  type="number"
                  min={0.1}
                  max={10}
                  step={0.1}
                  value={form.weight_reconciliation_tolerance_pct}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      weight_reconciliation_tolerance_pct: e.target.value,
                    }))
                  }
                />
                <span className={styles.fieldHelp}>
                  Maximum allowed disagreement between expected and observed
                  weight at second weigh.
                </span>
                {fieldErrors['weight_reconciliation_tolerance_pct'] ? (
                  <span className={styles.fieldError}>
                    {fieldErrors['weight_reconciliation_tolerance_pct']}
                  </span>
                ) : null}
              </label>

              <label className={styles.field}>
                <span>Draft autosave interval (seconds)</span>
                <input
                  type="number"
                  min={1}
                  max={60}
                  step={1}
                  value={form.autosave_draft_interval_sec}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      autosave_draft_interval_sec: e.target.value,
                    }))
                  }
                />
                <span className={styles.fieldHelp}>
                  How often the New Ticket page silently saves draft progress.
                </span>
                {fieldErrors['autosave_draft_interval_sec'] ? (
                  <span className={styles.fieldError}>
                    {fieldErrors['autosave_draft_interval_sec']}
                  </span>
                ) : null}
              </label>
            </div>
          </div>
        </Card>

        {/* -- Compliance toggles ---------------------------------------- */}
        <Card>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Compliance</h3>
            <div className={styles.toggleStack}>
              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.compliance_wtn_enabled}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      compliance_wtn_enabled: e.target.checked,
                    }))
                  }
                />
                <span>Generate Waste Transfer Notes</span>
              </label>
              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.compliance_hazardous_enabled}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      compliance_hazardous_enabled: e.target.checked,
                    }))
                  }
                />
                <span>Generate Hazardous Consignment Notes</span>
              </label>
              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.compliance_carrier_licence_required}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      compliance_carrier_licence_required: e.target.checked,
                    }))
                  }
                />
                <span>
                  Block ticket completion when the haulier has no valid carrier
                  licence
                </span>
              </label>
              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.compliance_ewc_required_on_waste_lines}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      compliance_ewc_required_on_waste_lines: e.target.checked,
                    }))
                  }
                />
                <span>Require an EWC code on every waste line</span>
              </label>
            </div>
          </div>
        </Card>

        {/* -- Stock alerts ---------------------------------------------- */}
        <Card>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Stock alert recipients</h3>
            <p className={styles.sectionHelp}>
              Email addresses notified when a product crosses its low or high
              stock threshold (up to 10).
            </p>
            <div className={styles.recipientList}>
              {form.stock_alert_email_recipients.map((email, idx) => (
                <div key={idx} className={styles.recipientRow}>
                  <input
                    type="email"
                    aria-label={`Recipient ${idx + 1}`}
                    value={email}
                    onChange={(e) => updateRecipient(idx, e.target.value)}
                    placeholder="name@example.com"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    icon={Trash2}
                    onClick={() => removeRecipient(idx)}
                    aria-label={`Remove recipient ${idx + 1}`}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              {form.stock_alert_email_recipients.length === 0 ? (
                <p className={styles.fieldHelp}>No recipients configured.</p>
              ) : null}
              {fieldErrors['stock_alert_email_recipients'] ? (
                <span className={styles.fieldError}>
                  {fieldErrors['stock_alert_email_recipients']}
                </span>
              ) : null}
            </div>
            <div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                icon={Plus}
                onClick={addRecipient}
                disabled={form.stock_alert_email_recipients.length >= 10}
              >
                Add recipient
              </Button>
            </div>
          </div>
        </Card>

        {error ? <p className={styles.error}>{error}</p> : null}

        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            loading={updateSettings.isPending}
          >
            Save changes
          </Button>
        </div>
      </form>
    </div>
  );
}
